/**
 * Matrices — morceaux d'interface : une matrice qu'on remplit au clavier, une
 * matrice de cases à toucher (pour choisir une case ou basculer 0 / 1).
 * Parenthèses dessinées en CSS ; cases de 44 px.
 */

import type { ReactNode } from 'react';
import { cn } from '@jeraisonne/ui';

/** Résumé court pour le lecteur d'écran (les formules ne sont pas relues). */
export function Live({ text }: { text: string }) {
  return (
    <p className="sr-only" aria-live="polite">
      {text}
    </p>
  );
}

/** Les grandes parenthèses autour d'une matrice. */
export function MatrixFrame({ children, cols }: { children: ReactNode; cols: number }) {
  return (
    <div
      className="relative inline-grid px-2"
      style={{ gridTemplateColumns: `repeat(${cols}, auto)` }}
    >
      <span
        aria-hidden
        className="border-ink-500 pointer-events-none absolute inset-y-0 left-0 w-2 rounded-l-xl border-y-0 border-l-2 border-r-0"
      />
      <span
        aria-hidden
        className="border-ink-500 pointer-events-none absolute inset-y-0 right-0 w-2 rounded-r-xl border-y-0 border-l-0 border-r-2"
      />
      {children}
    </div>
  );
}

/** « A = ( … ) » : nom, puis la matrice. */
export function Named({ name, children }: { name: ReactNode; children: ReactNode }) {
  return (
    <div className="flex max-w-full items-center gap-2 overflow-x-auto">
      <span className="text-ink-800 font-display shrink-0 text-lg font-bold italic">{name} =</span>
      {children}
    </div>
  );
}

/**
 * Matrice à remplir : chaque case est un champ texte (entier, décimal,
 * fraction). `bad[i][j]` souligne une case illisible ; `glow` éclaire une
 * ligne ou une colonne (le calcul ligne × colonne).
 */
export function MatrixInput({
  name,
  texts,
  onChange,
  bad,
  glowRow = null,
  glowCol = null,
}: {
  name: string;
  texts: string[][];
  onChange: (texts: string[][]) => void;
  bad?: boolean[][];
  glowRow?: number | null;
  glowCol?: number | null;
}) {
  const cols = texts[0]!.length;
  return (
    <Named name={name}>
      <MatrixFrame cols={cols}>
        {texts.map((row, i) =>
          row.map((t, j) => (
            <input
              key={`${i}-${j}`}
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              value={t}
              aria-label={`${name}, ligne ${i + 1}, colonne ${j + 1}`}
              aria-invalid={bad?.[i]?.[j] || undefined}
              onChange={(e) =>
                onChange(
                  texts.map((r, a) => r.map((v, b) => (a === i && b === j ? e.target.value : v))),
                )
              }
              className={cn(
                'text-ink-900 focus:ring-primary-400 m-0.5 h-11 w-16 rounded-lg border bg-transparent text-center text-base tabular-nums outline-none focus:ring-2',
                bad?.[i]?.[j] ? 'border-error-500' : 'border-ink-200',
                (glowRow === i || glowCol === j) && 'bg-primary-50',
              )}
            />
          )),
        )}
      </MatrixFrame>
    </Named>
  );
}

/**
 * Matrice de cases à toucher : choisir la case (i, j), ou basculer 0 / 1.
 * Noms de lignes et de colonnes au besoin (A, B, C…).
 */
export function MatrixGrid({
  name,
  values,
  labels,
  selected = null,
  onCell,
  disabled,
  cellLabel,
}: {
  name: ReactNode;
  values: string[][];
  /** noms des lignes et des colonnes */
  labels?: readonly string[];
  selected?: [number, number] | null;
  onCell?: (i: number, j: number) => void;
  disabled?: (i: number, j: number) => boolean;
  /** nom accessible d'une case */
  cellLabel: (i: number, j: number) => string;
}) {
  const cols = values[0]!.length;
  return (
    <Named name={name}>
      <div className="flex items-center gap-1">
        {labels && (
          <div className="flex flex-col pt-4" aria-hidden>
            {values.map((_, i) => (
              <span
                key={i}
                className="text-ink-400 my-0.5 flex h-11 items-center text-xs font-bold"
              >
                {labels[i]}
              </span>
            ))}
          </div>
        )}
        <div>
          {labels && (
            <div className="flex px-2" aria-hidden>
              {values[0]!.map((_, j) => (
                <span key={j} className="text-ink-400 m-0.5 w-11 text-center text-xs font-bold">
                  {labels[j]}
                </span>
              ))}
            </div>
          )}
          <MatrixFrame cols={cols}>
            {values.map((row, i) =>
              row.map((v, j) => {
                const on = selected !== null && selected[0] === i && selected[1] === j;
                const off = disabled?.(i, j) ?? false;
                return (
                  <button
                    key={`${i}-${j}`}
                    type="button"
                    disabled={off || !onCell}
                    aria-pressed={onCell && !off ? on : undefined}
                    aria-label={cellLabel(i, j)}
                    onClick={() => onCell?.(i, j)}
                    className={cn(
                      'font-display m-0.5 flex h-11 min-w-11 items-center justify-center rounded-lg px-1 text-base font-bold tabular-nums',
                      on
                        ? 'bg-primary-500 text-white'
                        : off
                          ? 'text-ink-300'
                          : onCell
                            ? 'text-ink-900 hover:bg-ink-100 border-ink-200 border'
                            : 'text-ink-900',
                    )}
                  >
                    {v}
                  </button>
                );
              }),
            )}
          </MatrixFrame>
        </div>
      </div>
    </Named>
  );
}
